/**
 * useCalendarState Hook
 * V226: 통합 캘린더 상태 관리 훅
 *
 * 캘린더 관련 상태와 유틸리티 함수를 제공합니다.
 */

import { useState, useMemo, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';
import type { DateRange, QuickPreset, CalendarMode } from './types';
import { QUICK_PRESETS } from './constants';

// dayjs 한국어 설정
dayjs.locale('ko');

interface UseCalendarStateOptions {
    /** 초기 날짜 */
    initialDate?: Date;
    /** 초기 범위 */
    initialRange?: DateRange;
    /** 캘린더 모드 */
    mode?: CalendarMode;
}

interface CalendarState {
    // 현재 표시 월
    currentMonth: Date;
    // 선택된 날짜 (single)
    selectedDate: Date | undefined;
    // 선택된 범위 (range)
    selectedRange: DateRange | undefined;
    // 선택된 프리셋
    selectedPreset: QuickPreset | undefined;
}

interface CalendarActions {
    // 날짜 선택
    selectDate: (date: Date | undefined) => void;
    // 범위 선택
    selectRange: (range: DateRange | undefined) => void;
    // 월 변경
    setMonth: (date: Date) => void;
    // 이전 월
    prevMonth: () => void;
    // 다음 월
    nextMonth: () => void;
    // 오늘로 이동
    goToToday: () => void;
    // 프리셋 적용
    applyPreset: (preset: QuickPreset) => void;
    // 초기화
    reset: () => void;
}

interface CalendarUtils {
    // 날짜 포맷
    formatDate: (date: Date, format?: string) => string;
    // 날짜 문자열 -> Date
    parseDate: (dateStr: string) => Date;
    // 오늘 여부
    isToday: (date: Date) => boolean;
    // 선택 여부
    isSelected: (date: Date) => boolean;
    // 범위 내 여부
    isInRange: (date: Date) => boolean;
    // 범위 시작
    isRangeStart: (date: Date) => boolean;
    // 범위 끝
    isRangeEnd: (date: Date) => boolean;
    // 비활성화 여부
    isDisabled: (date: Date, minDate?: Date, maxDate?: Date) => boolean;
    // 주말 여부
    isWeekend: (date: Date) => boolean;
    // 같은 월 여부
    isSameMonth: (date: Date, compareDate: Date) => boolean;
    // 캘린더 그리드 생성
    getCalendarGrid: (month: Date) => Date[][];
}

export interface UseCalendarStateReturn {
    state: CalendarState;
    actions: CalendarActions;
    utils: CalendarUtils;
}

/**
 * 프리셋에서 날짜 범위 계산
 */
export function getDateRangeFromPreset(preset: QuickPreset): DateRange {
    const today = dayjs().startOf('day');

    switch (preset) {
        case 'today':
            return { from: today.toDate(), to: today.toDate() };
        case 'yesterday':
            const yesterday = today.subtract(1, 'day');
            return { from: yesterday.toDate(), to: yesterday.toDate() };
        case 'last3Days':
            return { from: today.subtract(2, 'day').toDate(), to: today.toDate() };
        case 'last5Days':
            return { from: today.subtract(4, 'day').toDate(), to: today.toDate() };
        case 'lastWeek':
            return { from: today.subtract(6, 'day').toDate(), to: today.toDate() };
        case 'last2Weeks':
            return { from: today.subtract(13, 'day').toDate(), to: today.toDate() };
        case 'lastMonth':
            return { from: today.subtract(29, 'day').toDate(), to: today.toDate() };
        case 'last3Months':
            return { from: today.subtract(3, 'month').toDate(), to: today.toDate() };
        case 'last6Months':
            return { from: today.subtract(6, 'month').toDate(), to: today.toDate() };
        case 'thisMonth':
            return {
                from: today.startOf('month').toDate(),
                to: today.endOf('month').toDate(),
            };
        case 'thisYear':
            return {
                from: today.startOf('year').toDate(),
                to: today.endOf('year').toDate(),
            };
        case 'custom':
        default:
            return { from: today.toDate(), to: today.toDate() };
    }
}

/**
 * 캘린더 상태 관리 훅
 */
export function useCalendarState(
    options: UseCalendarStateOptions = {}
): UseCalendarStateReturn {
    const { initialDate, initialRange, mode = 'single' } = options;

    // 상태
    const [currentMonth, setCurrentMonth] = useState<Date>(
        initialDate || initialRange?.from || new Date()
    );
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
    const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(initialRange);
    const [selectedPreset, setSelectedPreset] = useState<QuickPreset | undefined>();

    // 액션
    const actions: CalendarActions = useMemo(
        () => ({
            selectDate: (date) => {
                setSelectedDate(date);
                setSelectedPreset(undefined);
                if (date) setCurrentMonth(date);
            },
            selectRange: (range) => {
                setSelectedRange(range);
                if (range?.from) setCurrentMonth(range.from);
            },
            setMonth: (date) => setCurrentMonth(date),
            prevMonth: () => setCurrentMonth((prev) => dayjs(prev).subtract(1, 'month').toDate()),
            nextMonth: () => setCurrentMonth((prev) => dayjs(prev).add(1, 'month').toDate()),
            goToToday: () => {
                const today = new Date();
                setCurrentMonth(today);
                if (mode === 'single') {
                    setSelectedDate(today);
                }
            },
            applyPreset: (preset) => {
                const range = getDateRangeFromPreset(preset);
                setSelectedRange(range);
                setSelectedPreset(preset);
                if (range.from) setCurrentMonth(range.from);
            },
            reset: () => {
                setSelectedDate(initialDate);
                setSelectedRange(initialRange);
                setSelectedPreset(undefined);
                setCurrentMonth(initialDate || initialRange?.from || new Date());
            },
        }),
        [mode, initialDate, initialRange]
    );

    // 유틸리티
    const utils: CalendarUtils = useMemo(
        () => ({
            formatDate: (date, format = 'YYYY-MM-DD') => dayjs(date).format(format),
            parseDate: (dateStr) => dayjs(dateStr).toDate(),
            isToday: (date) => dayjs(date).isSame(dayjs(), 'day'),
            isSelected: (date) => {
                if (mode === 'single' && selectedDate) {
                    return dayjs(date).isSame(selectedDate, 'day');
                }
                return false;
            },
            isInRange: (date) => {
                if (!selectedRange?.from) return false;
                const d = dayjs(date);
                const from = dayjs(selectedRange.from);
                const to = selectedRange.to ? dayjs(selectedRange.to) : from;
                return d.isAfter(from, 'day') && d.isBefore(to, 'day');
            },
            isRangeStart: (date) => {
                if (!selectedRange?.from) return false;
                return dayjs(date).isSame(selectedRange.from, 'day');
            },
            isRangeEnd: (date) => {
                if (!selectedRange?.to) return false;
                return dayjs(date).isSame(selectedRange.to, 'day');
            },
            isDisabled: (date, minDate, maxDate) => {
                const d = dayjs(date);
                if (minDate && d.isBefore(dayjs(minDate), 'day')) return true;
                if (maxDate && d.isAfter(dayjs(maxDate), 'day')) return true;
                return false;
            },
            isWeekend: (date) => {
                const day = dayjs(date).day();
                return day === 0 || day === 6;
            },
            isSameMonth: (date, compareDate) =>
                dayjs(date).isSame(compareDate, 'month'),
            getCalendarGrid: (month) => {
                const startOfMonth = dayjs(month).startOf('month');
                const endOfMonth = dayjs(month).endOf('month');
                const startDate = startOfMonth.startOf('week');
                const endDate = endOfMonth.endOf('week');

                const weeks: Date[][] = [];
                let current = startDate;
                let currentWeek: Date[] = [];

                while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
                    currentWeek.push(current.toDate());
                    if (currentWeek.length === 7) {
                        weeks.push(currentWeek);
                        currentWeek = [];
                    }
                    current = current.add(1, 'day');
                }

                if (currentWeek.length > 0) {
                    weeks.push(currentWeek);
                }

                return weeks;
            },
        }),
        [mode, selectedDate, selectedRange]
    );

    return {
        state: {
            currentMonth,
            selectedDate,
            selectedRange,
            selectedPreset,
        },
        actions,
        utils,
    };
}

export default useCalendarState;
